import { FC, FormEvent, useEffect } from 'react';
import { Button, Box, Typography, Switch, FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { PromoCreateDto } from '@libs/dto';
import { PromoType } from '@libs/types';
import { TextInput, Select, DateRangeInput } from '@carcareers/components';
import { useForm, useDrawer } from '@carcareers/contexts';
import { usePromo } from '@carcareers/hooks';

const PROMO_TYPE_OPTIONS = [
  { label: 'Percentage', value: PromoType.PERCENTAGE },
  { label: 'Fixed Amount', value: PromoType.FIXED },
];

interface PromoFormProps {
  type?: 'create' | 'update';
  onSuccess?: () => void;
}

export const PromoForm: FC<PromoFormProps> = ({ type = 'create', onSuccess }) => {
  const navigate = useNavigate();
  const { setOpen } = useDrawer();
  const { data, setData, setErrors, reset } = useForm<PromoCreateDto>();
  const { createPromo, updatePromo } = usePromo();

  useEffect(() => {
    if (type === 'create' && !data?.type) {
      setData('type', PromoType.PERCENTAGE);
      setData('isActive', true);
    }
  }, [type, data?.type, setData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const callbacks = onSuccess ? { onSuccess } : undefined;

    const formattedData = {
      ...data,
      discount: Number(data?.discount),
      startDate: new Date(data?.startDate || ''),
      endDate: new Date(data?.endDate || ''),
    };

    const response = type === 'create' ? await createPromo(formattedData, callbacks) : await updatePromo(formattedData, callbacks);

    if (response?.data) {
      reset();
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  return (
    <>
      <Typography variant="h6">Promo Details</Typography>
      <TextInput required label="Code" name="code" />
      <Select label="Discount Type" name="type" options={PROMO_TYPE_OPTIONS} />
      <TextInput
        required
        label={data?.type === PromoType.FIXED ? 'Discount Amount ($)' : 'Discount Percentage (%)'}
        name="discount"
        type="number"
      />
      <TextInput required label="Maximum Uses" name="maxUses" type="number" />
      {type === 'create' && (
        <FormControlLabel
          control={<Switch checked={Boolean(data?.isActive)} onChange={(e) => setData('isActive', e.target.checked)} />}
          label="Active"
        />
      )}
      <Typography variant="h6" sx={{ mt: 2 }}>
        Promo Validity
      </Typography>
      <DateRangeInput nameStart="startDate" nameEnd="endDate" />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          mt: 2,
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button
          size="large"
          variant="outlined"
          color="secondary"
          onClick={type === 'create' ? () => setOpen(false) : () => navigate(-1)}
        >
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          {type === 'create' ? 'Create' : 'Update'}
        </Button>
      </Box>
    </>
  );
};
